import { MetricDrilldown as SharedMetricDrilldown } from "@/components/metrics/metric-drilldown";
import {
  type CustomChannelMetrics,
  useCustomChannelMetricDrilldown,
} from "@/hooks/api/use-custom-channels";
import type { MetricsFilters } from "@/lib/filters";
import { METRIC_CONFIG } from "@/pages/custom-channels/custom-channels-list/custom-channels-metrics-config";

// Mapping from derived metrics to their base metrics for drilldown
// Amount metrics map to their count equivalents to reuse the same drilldown
const METRIC_TO_BASE: Record<string, keyof CustomChannelMetrics> = {
  opportunities_influenced_amount: "opportunities_influenced",
  opportunities_influenced_won_amount: "opportunities_influenced_won",
  opportunities_influenced_in_cycle_amount: "opportunities_influenced_in_cycle",
  opportunities_created_post_engagement_amount:
    "opportunities_created_post_engagement",
  progressed_opportunities_amount: "progressed_opportunities",
  opportunities_activated_amount: "opportunities_activated",
  opportunities_activated_won_amount: "opportunities_activated_won",
  // Per-entity metrics map to their entity totals
  engagements_per_account: "accounts_engaged",
  engagements_per_person: "people_engaged",
  engagements_per_deal_contact: "deal_contacts_engaged",
};

interface MetricDrilldownProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  channel: string | null;
  subchannel?: string;
  metric: keyof typeof METRIC_CONFIG;
  metricValue?: number | string;
  filters: MetricsFilters;
  metrics?: CustomChannelMetrics;
  drillDownLimit?: number;
  loadingStates?: Record<
    keyof CustomChannelMetrics,
    { isLoading: boolean; isError: boolean }
  >;
  rule_set_id?: string;
}

/**
 * Custom Channels-specific wrapper for the shared MetricDrilldown component.
 * Handles custom channel-specific data fetching and passes data to the generic component.
 */
export function MetricDrilldown(props: MetricDrilldownProps) {
  const {
    open,
    onOpenChange,
    channel,
    subchannel,
    metric,
    metricValue: providedMetricValue,
    filters,
    metrics,
    loadingStates,
    drillDownLimit,
    rule_set_id,
  } = props;

  // Map derived metrics (like amount metrics) to their base metrics for drilldown
  // This reuses the same drilldown table/columns for amount metrics as their count equivalents
  const drilldownMetric = (METRIC_TO_BASE[metric] ||
    metric) as keyof CustomChannelMetrics;

  // Check if drilldown should be enabled based on metric value and limit
  // Use the provided metric value from the table row, or fall back to metrics query
  const metricValue = providedMetricValue ?? metrics?.[metric];
  const isMetricValueNumber = typeof metricValue === "number";
  // Skip limit check for amount/currency metrics since they show dollar values, not row counts
  const isAmountMetric = metric.toString().includes("amount");
  const isDrilldownLimitExceeded =
    drillDownLimit !== undefined &&
    isMetricValueNumber &&
    !isAmountMetric &&
    metricValue >= drillDownLimit;

  // Fetch drilldown data only if limit is not exceeded
  // Must call hooks unconditionally before any early returns
  const { data: drilldownData, isLoading } = useCustomChannelMetricDrilldown({
    channel: channel || "",
    subchannel,
    metric: drilldownMetric, // Use the base metric for drilldown
    filters,
    enabled: open && !!channel && !isDrilldownLimitExceeded,
    rule_set_id,
  });

  // Use drilldownMetric's config for the actual drilldown data structure
  // Amount/per-entity metrics will use their base metric's config (e.g., opportunities_influenced_won)
  const drilldownConfig = METRIC_CONFIG[drilldownMetric];
  if (!drilldownConfig) return null;

  // Build entity name for CSV export
  const entityName = `${channel}${subchannel ? `-${subchannel}` : ""}`;

  const shouldShowLoading =
    isLoading || (open && !drilldownData && !isDrilldownLimitExceeded);

  // Create metrics object with the provided value for limit checking
  // This ensures the limit check works immediately without waiting for API query
  const metricsWithProvidedValue =
    providedMetricValue !== undefined
      ? { ...metrics, [metric]: providedMetricValue }
      : metrics;

  // Override loading state for the metric if we have a provided value
  // This ensures the metric value displays immediately in the dialog header
  const loadingStatesWithProvidedValue =
    providedMetricValue !== undefined && loadingStates
      ? { ...loadingStates, [metric]: { isLoading: false, isError: false } }
      : loadingStates;

  return (
    <SharedMetricDrilldown
      open={open}
      onOpenChange={onOpenChange}
      metric={metric as string}
      metricConfig={drilldownConfig as never} // Use drilldown config for table structure
      metrics={metricsWithProvidedValue as Record<string, unknown>}
      drilldownData={drilldownData}
      isLoadingDrilldown={shouldShowLoading}
      loadingStates={loadingStatesWithProvidedValue}
      drillDownLimit={drillDownLimit}
      entityName={entityName}
      tableName={`custom-channels::drilldown::${drilldownMetric}`} // Use base metric for table name
    />
  );
}
